import { createTheme } from '@mui/material/styles';
import colors from './colors';

export function createAppTheme(mode) {
  const c = colors[mode];
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.accentPrimary,
        light: c.accentTertiary,
        dark: isDark ? '#4F46E5' : '#4338CA',
      },
      secondary: {
        main: c.accentSecondary,
      },
      success: { main: c.success },
      warning: { main: c.warning },
      error: { main: c.error },
      info: { main: c.info },
      background: {
        default: c.bgPrimary,
        paper: c.bgCard,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
        disabled: c.textMuted,
      },
      divider: c.borderPrimary,
    },

    typography: {
      fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500, color: c.textSecondary },
      subtitle2: { fontWeight: 500, color: c.textSecondary, fontSize: '0.8125rem' },
      body1: { fontWeight: 400, lineHeight: 1.6 },
      body2: { fontWeight: 400, lineHeight: 1.6, fontSize: '0.875rem' },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
      caption: { color: c.textMuted, fontSize: '0.75rem' },
      overline: { fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6875rem' },
    },

    shape: {
      borderRadius: 12,
    },

    shadows: [
      'none',
      isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.08)',
      isDark ? '0 2px 6px rgba(0,0,0,0.5)' : '0 2px 6px rgba(0,0,0,0.06)',
      isDark ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.08)',
      isDark ? '0 6px 16px rgba(0,0,0,0.6)' : '0 6px 16px rgba(0,0,0,0.1)',
      isDark ? '0 8px 24px rgba(0,0,0,0.6)' : '0 8px 24px rgba(0,0,0,0.1)',
      ...Array(19).fill(isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.08)'),
    ],

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.bgPrimary,
            color: c.textPrimary,
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.accentPrimary}40 transparent`,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: `${c.accentPrimary}40`,
              borderRadius: 3,
            },
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.accentPrimary}40 transparent`,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgCard,
            border: `1px solid ${c.borderPrimary}`,
            borderRadius: 16,
            backgroundImage: 'none',
            boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.06)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              borderColor: c.borderAccent,
              transform: 'translateY(-4px)',
              boxShadow: isDark
                ? `0 12px 40px rgba(99, 102, 241, 0.15), 0 0 0 1px ${c.borderAccent}`
                : `0 8px 32px rgba(0,0,0,0.12)`,
            },
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '24px 28px',
            '&:last-child': { paddingBottom: 24 },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
            padding: '10px 22px',
            fontSize: '0.875rem',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            letterSpacing: '0.01em',
          },
          contained: {
            background: c.gradient,
            color: '#FFFFFF',
            boxShadow: `0 4px 14px ${c.accentGlow}`,
            '&:hover': {
              background: c.gradient,
              boxShadow: `0 8px 28px ${c.accentPrimary}50`,
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: `0 2px 8px ${c.accentGlow}`,
            },
            '&.Mui-disabled': {
              background: isDark ? '#1A1A3E' : '#E5E7EB',
              color: c.textMuted,
              boxShadow: 'none',
            },
          },
          containedSuccess: {
            background: `linear-gradient(135deg, ${c.success}, ${isDark ? '#059669' : '#047857'})`,
            boxShadow: `0 4px 14px ${c.success}40`,
            '&:hover': {
              background: `linear-gradient(135deg, ${c.success}, ${isDark ? '#059669' : '#047857'})`,
              boxShadow: `0 8px 28px ${c.success}50`,
              transform: 'translateY(-2px)',
            },
          },
          containedError: {
            background: `linear-gradient(135deg, ${c.error}, ${isDark ? '#DC2626' : '#B91C1C'})`,
            boxShadow: `0 4px 14px ${c.error}40`,
            '&:hover': {
              background: `linear-gradient(135deg, ${c.error}, ${isDark ? '#DC2626' : '#B91C1C'})`,
              boxShadow: `0 8px 28px ${c.error}50`,
              transform: 'translateY(-2px)',
            },
          },
          containedWarning: {
            background: `linear-gradient(135deg, ${c.warning}, ${isDark ? '#D97706' : '#B45309'})`,
            boxShadow: `0 4px 14px ${c.warning}40`,
            '&:hover': {
              background: `linear-gradient(135deg, ${c.warning}, ${isDark ? '#D97706' : '#B45309'})`,
              boxShadow: `0 8px 28px ${c.warning}50`,
              transform: 'translateY(-2px)',
            },
          },
          containedInfo: {
            background: c.gradient,
            boxShadow: `0 4px 14px ${c.accentGlow}`,
            '&:hover': {
              background: c.gradient,
              boxShadow: `0 8px 28px ${c.accentPrimary}50`,
              transform: 'translateY(-2px)',
            },
          },
          outlined: {
            borderColor: c.borderAccent,
            color: c.accentPrimary,
            borderWidth: 1.5,
            '&:hover': {
              borderColor: c.accentPrimary,
              backgroundColor: `${c.accentPrimary}12`,
              borderWidth: 1.5,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 16px ${c.accentPrimary}15`,
            },
          },
          text: {
            color: c.textSecondary,
            '&:hover': {
              backgroundColor: `${c.accentPrimary}10`,
              color: c.accentPrimary,
            },
          },
          sizeSmall: {
            padding: '6px 14px',
            fontSize: '0.8125rem',
          },
          sizeLarge: {
            padding: '14px 32px',
            fontSize: '1rem',
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            color: c.textSecondary,
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: `${c.accentPrimary}15`,
              color: c.accentPrimary,
              transform: 'scale(1.08)',
            },
          },
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: c.bgInput,
              borderRadius: 12,
              fontSize: '0.875rem',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '& fieldset': {
                borderColor: c.borderPrimary,
                transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
              },
              '&:hover fieldset': {
                borderColor: c.borderAccent,
              },
              '&.Mui-focused fieldset': {
                borderColor: c.accentPrimary,
                borderWidth: 1.5,
                boxShadow: `0 0 0 3px ${c.accentGlow}`,
              },
              '&.Mui-error fieldset': {
                borderColor: c.error,
                boxShadow: `0 0 0 3px ${c.error}15`,
              },
            },
            '& .MuiInputLabel-root': {
              color: c.textMuted,
              fontSize: '0.8rem',
              fontWeight: 500,
              '&.Mui-focused': {
                color: c.accentPrimary,
              },
            },
            '& .MuiInputBase-input': {
              color: c.textPrimary,
              padding: '10px 14px',
              '&::placeholder': {
                color: c.textMuted,
                opacity: 1,
              },
            },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgInput,
            borderRadius: 12,
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: c.borderPrimary,
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: c.borderAccent,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: c.accentPrimary,
              borderWidth: 1.5,
              boxShadow: `0 0 0 3px ${c.accentGlow}`,
            },
          },
          icon: {
            color: c.textMuted,
            transition: 'transform 0.2s ease',
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: c.bgCard,
            border: `1px solid ${c.borderPrimary}`,
            borderRadius: 14,
            boxShadow: isDark ? '0 12px 48px rgba(0,0,0,0.6)' : '0 12px 48px rgba(0,0,0,0.15)',
            marginTop: 6,
            backdropFilter: 'blur(20px)',
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            padding: '8px 16px',
            borderRadius: 8,
            margin: '2px 6px',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: `${c.accentPrimary}12`,
            },
            '&.Mui-selected': {
              backgroundColor: `${c.accentPrimary}18`,
              color: c.accentPrimary,
              '&:hover': {
                backgroundColor: `${c.accentPrimary}25`,
              },
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: c.bgCard,
            border: `1px solid ${c.borderPrimary}`,
            borderRadius: 20,
            backgroundImage: 'none',
            boxShadow: isDark ? '0 24px 80px rgba(0,0,0,0.7)' : '0 24px 80px rgba(0,0,0,0.18)',
            animation: 'scaleIn 0.2s ease-out',
          },
          backdrop: {
            backgroundColor: c.bgOverlay,
            backdropFilter: 'blur(12px)',
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.125rem',
            fontWeight: 700,
            padding: '24px 28px 16px',
            color: c.textPrimary,
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '8px 28px 24px',
          },
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 28px 24px',
            gap: 8,
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 28,
            transition: 'all 0.2s ease',
          },
          filled: {
            border: 'none',
          },
          filledSuccess: {
            backgroundColor: c.successBg,
            color: c.success,
          },
          filledError: {
            backgroundColor: c.errorBg,
            color: c.error,
          },
          filledWarning: {
            backgroundColor: c.warningBg,
            color: c.warning,
          },
          filledInfo: {
            backgroundColor: c.infoBg,
            color: c.info,
          },
          filledPrimary: {
            backgroundColor: `${c.accentPrimary}18`,
            color: c.accentPrimary,
          },
          outlined: {
            borderColor: c.borderPrimary,
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 42,
          },
          indicator: {
            height: 2,
            borderRadius: 1,
            background: c.gradient,
          },
          flexContainer: {
            gap: 4,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 42,
            padding: '8px 16px',
            borderRadius: '8px 8px 0 0',
            color: c.textMuted,
            transition: 'all 0.2s ease',
            '&:hover': {
              color: c.textPrimary,
              backgroundColor: `${c.accentPrimary}08`,
            },
            '&.Mui-selected': {
              color: c.accentPrimary,
              fontWeight: 600,
            },
          },
        },
      },

      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: 'separate',
            borderSpacing: '0 3px',
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isDark ? c.bgElevated : c.bgInput,
              color: c.textMuted,
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderBottom: 'none',
              padding: '12px 16px',
              whiteSpace: 'nowrap',
              '&:first-of-type': { borderRadius: '10px 0 0 10px' },
              '&:last-of-type': { borderRadius: '0 10px 10px 0' },
            },
          },
        },
      },

      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root': {
              transition: 'background-color 0.2s ease, transform 0.2s ease',
              '&:hover': {
                backgroundColor: `${c.accentPrimary}08`,
              },
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${c.borderPrimary}`,
            padding: '14px 16px',
            fontSize: '0.875rem',
            color: c.textPrimary,
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 44,
            height: 24,
            padding: 0,
          },
          switchBase: {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              '& + .MuiSwitch-track': {
                background: c.gradient,
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: '#fff',
              },
            },
          },
          thumb: {
            width: 20,
            height: 20,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          },
          track: {
            borderRadius: 12,
            backgroundColor: isDark ? '#2A2A4A' : '#D1D5DB',
            opacity: 1,
          },
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: c.textMuted,
            '&.Mui-checked': {
              color: c.accentPrimary,
            },
          },
        },
      },

      MuiSlider: {
        styleOverrides: {
          root: {
            color: c.accentPrimary,
            height: 6,
          },
          thumb: {
            width: 18,
            height: 18,
            backgroundColor: '#fff',
            border: `2px solid ${c.accentPrimary}`,
            '&:hover': {
              boxShadow: `0 0 0 8px ${c.accentPrimary}20`,
            },
          },
          track: {
            background: c.gradient,
            border: 'none',
          },
          rail: {
            backgroundColor: isDark ? '#2A2A4A' : '#E5E7EB',
          },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 6,
            backgroundColor: isDark ? '#1A1A3E' : '#E5E7EB',
          },
          bar: {
            borderRadius: 4,
            background: c.gradient,
          },
        },
      },

      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: c.accentPrimary,
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            fontSize: '0.875rem',
            border: '1px solid',
          },
          standardSuccess: {
            backgroundColor: c.successBg,
            borderColor: `${c.success}30`,
            color: c.success,
            '& .MuiAlert-icon': { color: c.success },
          },
          standardError: {
            backgroundColor: c.errorBg,
            borderColor: `${c.error}30`,
            color: c.error,
            '& .MuiAlert-icon': { color: c.error },
          },
          standardWarning: {
            backgroundColor: c.warningBg,
            borderColor: `${c.warning}30`,
            color: c.warning,
            '& .MuiAlert-icon': { color: c.warning },
          },
          standardInfo: {
            backgroundColor: c.infoBg,
            borderColor: `${c.info}30`,
            color: c.info,
            '& .MuiAlert-icon': { color: c.info },
          },
        },
      },

      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiPaper-root': {
              borderRadius: 12,
              border: `1px solid ${c.borderPrimary}`,
            },
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? c.bgElevated : c.textPrimary,
            color: isDark ? c.textPrimary : '#fff',
            fontSize: '0.75rem',
            fontWeight: 500,
            borderRadius: 8,
            padding: '6px 12px',
            border: `1px solid ${c.borderPrimary}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          },
          arrow: {
            color: isDark ? c.bgElevated : c.textPrimary,
          },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: {
            background: c.gradientSubtle,
            color: c.accentPrimary,
            fontWeight: 600,
            border: `2px solid ${c.borderPrimary}`,
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: c.borderPrimary,
          },
        },
      },

      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgCard,
            border: `1px solid ${c.borderPrimary}`,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: '8px 0',
              borderColor: c.borderAccent,
            },
          },
        },
      },

      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            minHeight: 48,
            '&.Mui-expanded': { minHeight: 48 },
          },
          content: {
            '&.Mui-expanded': { margin: '12px 0' },
          },
        },
      },

      MuiStepper: {
        styleOverrides: {
          root: {
            '& .MuiStepIcon-root': {
              color: isDark ? '#2A2A4A' : '#E5E7EB',
              '&.Mui-active': { color: c.accentPrimary },
              '&.Mui-completed': { color: c.success },
            },
            '& .MuiStepLabel-label': {
              color: c.textMuted,
              fontSize: '0.8125rem',
              '&.Mui-active': { color: c.accentPrimary, fontWeight: 600 },
              '&.Mui-completed': { color: c.success },
            },
            '& .MuiStepConnector-line': {
              borderColor: c.borderPrimary,
            },
          },
        },
      },

      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: '0.875rem',
            color: c.textSecondary,
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: c.textMuted,
            fontSize: '0.875rem',
          },
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .MuiInputLabel-root': {
              color: c.textMuted,
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.bgSidebar,
            borderRight: `1px solid ${c.borderPrimary}`,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? `${c.bgSecondary}CC` : `${c.bgSecondary}EE`,
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${c.borderPrimary}`,
            boxShadow: 'none',
            color: c.textPrimary,
          },
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: c.bgCard,
            border: `1px solid ${c.borderPrimary}`,
            borderRadius: 12,
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
          },
          option: {
            fontSize: '0.875rem',
            borderRadius: 8,
            margin: '2px 6px',
            '&:hover': {
              backgroundColor: `${c.accentPrimary}12`,
            },
            '&.Mui-focused': {
              backgroundColor: `${c.accentPrimary}12`,
            },
          },
          tag: {
            borderRadius: 6,
          },
        },
      },

      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: c.bgCard,
            border: `1px solid ${c.borderPrimary}`,
            borderRadius: 12,
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
          },
        },
      },

      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgInput,
            borderRadius: 10,
            border: `1px solid ${c.borderPrimary}`,
            padding: 2,
            gap: 2,
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: '8px !important',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            padding: '6px 14px',
            color: c.textMuted,
            '&.Mui-selected': {
              background: c.gradient,
              color: '#fff',
              '&:hover': {
                background: c.gradient,
              },
            },
            '&:hover': {
              backgroundColor: `${c.accentPrimary}10`,
            },
          },
        },
      },

      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              borderRadius: 10,
              border: `1px solid ${c.borderPrimary}`,
              color: c.textSecondary,
              fontWeight: 500,
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                background: c.gradient,
                color: '#fff',
                borderColor: 'transparent',
                boxShadow: `0 4px 14px ${c.accentGlow}`,
              },
              '&:hover': {
                backgroundColor: `${c.accentPrimary}10`,
                borderColor: c.borderAccent,
              },
            },
          },
        },
      },

      MuiGrid: {
        styleOverrides: {
          root: {},
        },
      },
    },
  });
}
